import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { PlaceholderLoader } from '@hotelplan/components.common.placeholder-loader';
import { FdrImage } from '@hotelplan/supergraph-api';
import { TBookmarkItem } from 'components/domain/fdr-bookmark/fdr-bookmark-types';
import { getBookmarkedObjectItemFromFdrBookmark } from 'components/domain/fdr-bookmark/fdr-bookmark.utils';
import FdrImgWithCopyright from 'components/domain/fdr-copyright/fdr-img-with-copyright';
import {
  ImageWrap,
  ModalCloseButton,
  SearchDetailsContent,
  ShareOverlayWrap,
  ShareSocialNetworksList,
} from 'components/domain/fdr-share-overlay/fdr-share-overlay.styles';
import { EShareType } from 'components/domain/fdr-share-overlay/fdr-share-overlay.types';
import { useShareLinks } from 'components/domain/fdr-share-overlay/fdr-use-share-links';
import Print from 'components/domain/share-overlay/Print';
import ShareButton from 'components/domain/share-overlay/ShareButton';
import { useFdrPdfUploadMutation } from 'schemas/query/pdf/fdr-pdf-upload.generated';
import { FlaglineText, H4Heading } from 'theme/headings';

interface ISharePdfOverlayProps {
  items: Array<TBookmarkItem>;
  image: FdrImage;
  pageTitle: string;
  overlayTitle: string;
  trackFn(category: string, language: string): void;
  whiteList?: Array<EShareType>;
  onClose?(): void;
}

interface IProductPdfUrlData {
  id: string;
  name: string;
  link: string;
}

const iconPlaceholder = (
  <PlaceholderLoader
    width={42}
    height={42}
    speed={2}
    style={{ width: `42px`, height: '42px' }}
    uid="iconPlaceholder"
  >
    <rect width={42} height={42} rx={8} />
  </PlaceholderLoader>
);

const SharePdfOverlay: React.FC<ISharePdfOverlayProps> = ({
  onClose,
  image,
  pageTitle,
  overlayTitle,
  whiteList = [],
  trackFn,
  items,
}) => {
  const [, { language }] = useTranslation('common');
  const [urlPdfList, setUrlPdfList] = useState<IProductPdfUrlData[]>([]);
  const linksShare = useShareLinks(pageTitle, urlPdfList, whiteList);
  const [upload, { loading }] = useFdrPdfUploadMutation();

  useEffect(() => {
    const productIds = items
      .map(item => getBookmarkedObjectItemFromFdrBookmark(item)?.id)
      .filter((id): id is string => Boolean(id));

    if (productIds.length === 0) {
      setUrlPdfList([]);
      return;
    }

    upload({ variables: { input: { productIds } } })
      .then(response => {
        const results = response?.data?.fdrUploadProductPdf?.results || [];
        const pdfData: IProductPdfUrlData[] = results
          .filter(item => item.success && item.product && item.url)
          .map(item => ({
            id: item.product.id,
            name: item.product.name,
            link: item.url,
          }));
        setUrlPdfList(pdfData);
      })
      .catch(err => {
        console.error(`PDF upload error:`, err);
        setUrlPdfList([]);
      });
  }, [items, upload]);

  return (
    <ShareOverlayWrap className="share-overlay">
      <FlaglineText className="tagline-text">{overlayTitle}</FlaglineText>
      <SearchDetailsContent>
        <ImageWrap>
          {image && <FdrImgWithCopyright image={image} className="image" />}
        </ImageWrap>
        <H4Heading className="heading">{pageTitle}</H4Heading>
      </SearchDetailsContent>

      <ShareSocialNetworksList>
        {loading ? (
          <div style={{ display: 'flex', columnGap: 16 }}>
            {whiteList.map((_, idx) => (
              <React.Fragment key={idx}>{iconPlaceholder}</React.Fragment>
            ))}
          </div>
        ) : (
          Object.entries(linksShare).map(([platform, linkData]) => {
            if (!linkData) return null;
            if (platform === 'print') {
              return <Print key={platform} icon={linkData.icon} />;
            }
            return (
              <ShareButton
                key={platform}
                item={linkData}
                trackFn={() => trackFn(platform, language)}
                platform={platform}
              />
            );
          })
        )}
      </ShareSocialNetworksList>

      <ModalCloseButton onClick={onClose}>{`Close window`}</ModalCloseButton>
    </ShareOverlayWrap>
  );
};

export default SharePdfOverlay;
